import React, { useState } from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiArrowRight, FiUser, FiBriefcase, FiMail, FiLink, FiClock, FiCalendar, 
  FiCamera, FiMapPin, FiList, FiHeadphones, FiTag 
} = FiIcons;

const TermsForm = ({ initialData, onSubmit }) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.educator_name.trim()) newErrors.educator_name = 'Educator name is required';
    if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
    if (!formData.business_location.trim()) newErrors.business_location = 'Business location is required';
    if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
    if (!formData.booking_link.trim()) newErrors.booking_link = 'Booking link is required';
    if (!formData.services_list.trim()) newErrors.services_list = 'Services list is required';

    // Email validation
    if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }

    // URL validation
    if (formData.booking_link && !formData.booking_link.startsWith('http')) {
      newErrors.booking_link = 'Please enter a valid URL (starting with http:// or https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const inputClasses = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors";
  const errorClasses = "border-red-300 focus:ring-red-500 focus:border-red-500";

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Business Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center mb-6">
            <SafeIcon icon={FiBriefcase} className="text-xl text-indigo-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Business Information</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiUser} className="inline mr-1" /> Educator Name *
              </label>
              <input
                type="text"
                value={formData.educator_name}
                onChange={(e) => handleChange('educator_name', e.target.value)}
                className={`${inputClasses} ${errors.educator_name ? errorClasses : ''}`}
                placeholder="John Doe"
              />
              {errors.educator_name && <p className="text-red-500 text-sm mt-1">{errors.educator_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiBriefcase} className="inline mr-1" /> Business Name *
              </label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                className={`${inputClasses} ${errors.business_name ? errorClasses : ''}`}
                placeholder="ABC Learning Center"
              />
              {errors.business_name && <p className="text-red-500 text-sm mt-1">{errors.business_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiMapPin} className="inline mr-1" /> Business Location *
              </label>
              <input
                type="text"
                value={formData.business_location}
                onChange={(e) => handleChange('business_location', e.target.value)}
                className={`${inputClasses} ${errors.business_location ? errorClasses : ''}`}
                placeholder="New York, NY"
              />
              {errors.business_location && <p className="text-red-500 text-sm mt-1">{errors.business_location}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiMail} className="inline mr-1" /> Contact Email *
              </label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                className={`${inputClasses} ${errors.contact_email ? errorClasses : ''}`}
                placeholder="contact@example.com"
              />
              {errors.contact_email && <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiLink} className="inline mr-1" /> Booking Link *
              </label>
              <input
                type="url"
                value={formData.booking_link}
                onChange={(e) => handleChange('booking_link', e.target.value)}
                className={`${inputClasses} ${errors.booking_link ? errorClasses : ''}`}
                placeholder="https://example.com/booking"
              />
              {errors.booking_link && <p className="text-red-500 text-sm mt-1">{errors.booking_link}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiList} className="inline mr-1" /> Services List *
              </label>
              <textarea
                value={formData.services_list}
                onChange={(e) => handleChange('services_list', e.target.value)}
                rows={4}
                className={`${inputClasses} ${errors.services_list ? errorClasses : ''}`}
                placeholder="- Online tutoring sessions&#10;- Group workshops&#10;- One-on-one coaching&#10;- Exam preparation"
              />
              {errors.services_list && <p className="text-red-500 text-sm mt-1">{errors.services_list}</p>}
            </div>
          </div>
        </motion.div>

        {/* Policies & Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center mb-6">
            <SafeIcon icon={FiClock} className="text-xl text-indigo-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Policies & Settings</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Window (Hours)
              </label>
              <input
                type="number"
                value={formData.cancellation_window_hours}
                onChange={(e) => handleChange('cancellation_window_hours', parseInt(e.target.value) || 0)}
                className={inputClasses}
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reschedule Window (Hours)
              </label>
              <input
                type="number"
                value={formData.reschedule_window_hours}
                onChange={(e) => handleChange('reschedule_window_hours', parseInt(e.target.value) || 0)}
                className={inputClasses}
                min="1"
              />
            </div>
            
            {/* One-off Purchase Expiration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                One-off class credits expiration (Months)
              </label>
              <input
                type="number"
                value={formData.credit_expiration_months}
                onChange={(e) => handleChange('credit_expiration_months', parseInt(e.target.value) || 0)}
                className={inputClasses}
                min="1"
              />
            </div>
            
            {/* Subscription Expiration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription-based class credits expiration (Days)
              </label>
              <select
                value={formData.subscription_expiration_days}
                onChange={(e) => handleChange('subscription_expiration_days', e.target.value)}
                className={inputClasses}
              >
                <option value="7">7 Days</option>
                <option value="30">30 Days</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiCamera} className="inline mr-1" /> Camera Policy
              </label>
              <select
                value={formData.camera_policy}
                onChange={(e) => handleChange('camera_policy', e.target.value)}
                className={inputClasses}
              >
                <option value="Required">Required</option>
                <option value="Optional">Optional</option>
                <option value="Not required">Not required</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiHeadphones} className="inline mr-1" /> Headphones Policy
              </label>
              <select
                value={formData.headphones_policy}
                onChange={(e) => handleChange('headphones_policy', e.target.value)}
                className={inputClasses}
              >
                <option value="Required">Required</option>
                <option value="Optional">Optional</option>
                <option value="Not required">Not required</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                In-Person Services Offered
              </label>
              <select
                value={formData.in_person_offered}
                onChange={(e) => handleChange('in_person_offered', e.target.value)}
                className={inputClasses}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiCalendar} className="inline mr-1" /> Last Updated Date
              </label>
              <input
                type="date"
                value={formData.last_updated_date}
                onChange={(e) => handleChange('last_updated_date', e.target.value)}
                className={inputClasses}
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Classroom Rules
              </label>
              <textarea
                value={formData.classroom_rules}
                onChange={(e) => handleChange('classroom_rules', e.target.value)}
                rows={3}
                className={inputClasses}
                placeholder="Additional classroom rules and expectations..."
              />
            </div>
            {formData.in_person_offered === 'Yes' && (
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  In-Person Service Details
                </label>
                <textarea
                  value={formData.in_person_details}
                  onChange={(e) => handleChange('in_person_details', e.target.value)}
                  rows={3}
                  className={inputClasses}
                  placeholder="Describe your in-person service requirements, location, safety protocols, etc."
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center"
        >
          <button
            type="submit"
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center shadow-lg hover:shadow-xl"
            style={{ backgroundColor: '#28B07F' }}
          >
            Generate Terms & Conditions
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default TermsForm;